import type { SimulationState, ThemeData } from '../types';
import type { GameEvent } from '../events/GameEvents';
import type { SeededRandom } from '../rng/SeededRandom';

interface AchievementDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  check: (state: SimulationState) => boolean;
}

const ACHIEVEMENTS: AchievementDef[] = [
  // ─── Early Game ────────────────────────────────────────
  { id: 'first_game', name: 'Hello World', description: 'Release your first game', icon: '🎮',
    check: (s) => s.company.totalProductsReleased >= 1 },
  { id: 'first_team', name: 'Team Player', description: 'Create your first team', icon: '👥',
    check: (s) => s.teams.length >= 1 },
  { id: 'first_hire', name: 'Growing Studio', description: 'Hire your first employee', icon: '🤝',
    check: (s) => s.staff.length >= 2 },

  // ─── Products ──────────────────────────────────────────
  { id: 'five_games', name: 'Prolific', description: 'Release 5 games', icon: '📀',
    check: (s) => s.company.totalProductsReleased >= 5 },
  { id: 'ten_games', name: 'Veteran Studio', description: 'Release 10 games', icon: '🏭',
    check: (s) => s.company.totalProductsReleased >= 10 },
  { id: 'twenty_games', name: 'Factory Floor', description: 'Release 20 games', icon: '🔥',
    check: (s) => s.company.totalProductsReleased >= 20 },

  // ─── Quality ───────────────────────────────────────────
  { id: 'first_hof', name: 'Hall of Fame', description: 'Get a game into the Hall of Fame', icon: '⭐',
    check: (s) => s.company.hallOfFameCount >= 1 },
  { id: 'three_hof', name: 'Golden Studio', description: 'Get 3 games into the Hall of Fame', icon: '🏆',
    check: (s) => s.company.hallOfFameCount >= 3 },
  { id: 'five_hof', name: 'Legendary', description: 'Get 5 games into the Hall of Fame', icon: '👑',
    check: (s) => s.company.hallOfFameCount >= 5 },
  { id: 'perfect_10', name: 'Perfect Score', description: 'Get a 10/10 review', icon: '💯',
    check: (s) => s.products.some((p) => p.reviewScores.some((r) => r >= 10)) },
  { id: 'flop', name: 'Flop', description: 'Release a game with average review below 3', icon: '💀',
    check: (s) => s.products.some((p) => p.averageReview < 3) },

  // ─── Money ─────────────────────────────────────────────
  { id: 'revenue_100k', name: 'Six Figures', description: 'Earn $100K total revenue', icon: '💰',
    check: (s) => s.company.totalRevenue >= 100000 },
  { id: 'revenue_1m', name: 'Millionaire', description: 'Earn $1M total revenue', icon: '💎',
    check: (s) => s.company.totalRevenue >= 1000000 },
  { id: 'revenue_10m', name: 'Tycoon', description: 'Earn $10M total revenue', icon: '🏦',
    check: (s) => s.company.totalRevenue >= 10000000 },
  { id: 'debt_survivor', name: 'Debt Survivor', description: 'Recover from 4+ weeks in debt', icon: '😅',
    check: (s) => s.company.weeksInDebt === 0 && s.products.length > 0 && s.company.funds > 0 },

  // ─── Staff ─────────────────────────────────────────────
  { id: 'staff_10', name: 'Growing Team', description: 'Have 10 employees', icon: '🏢',
    check: (s) => s.staff.length >= 10 },
  { id: 'staff_20', name: 'Big Studio', description: 'Have 20 employees', icon: '🏗',
    check: (s) => s.staff.length >= 20 },
  { id: 'staff_30', name: 'AAA Studio', description: 'Have 30 employees', icon: '🌟',
    check: (s) => s.staff.length >= 30 },
  { id: 'max_level_staff', name: 'Master Dev', description: 'Have a staff member reach level 20', icon: '🧙',
    check: (s) => s.staff.some((st) => st.level >= 20) },
  { id: 'fired_someone', name: 'Tough Call', description: 'Fire an employee', icon: '📦',
    check: (s) => s.tutorialTipsShown.includes('achievement_fired') },

  // ─── Office ────────────────────────────────────────────
  { id: 'office_max', name: 'HQ Unlocked', description: 'Upgrade to the HQ Building', icon: '🏰',
    check: (s) => s.office.level >= 5 },

  // ─── Company ───────────────────────────────────────────
  { id: 'level_5', name: 'Established', description: 'Reach company level 5', icon: '📈',
    check: (s) => s.company.level >= 5 },
  { id: 'level_10', name: 'Industry Leader', description: 'Reach company level 10', icon: '📊',
    check: (s) => s.company.level >= 10 },
  { id: 'fans_10k', name: 'Fan Favorite', description: 'Reach 10,000 fans', icon: '❤️',
    check: (s) => s.company.fans >= 10000 },
  { id: 'fans_100k', name: 'Viral', description: 'Reach 100,000 fans', icon: '🌐',
    check: (s) => s.company.fans >= 100000 },

  // ─── Milestones ────────────────────────────────────────
  { id: 'survive_5', name: 'Staying Power', description: 'Survive 5 years', icon: '📅',
    check: (s) => s.company.currentYear >= 5 },
  { id: 'survive_10', name: 'Decade', description: 'Survive 10 years', icon: '🎂',
    check: (s) => s.company.currentYear >= 10 },
  { id: 'survive_20', name: 'Legacy', description: 'Complete the full 20-year campaign', icon: '🎖',
    check: (s) => s.company.currentYear >= 20 },

  // ─── Special ───────────────────────────────────────────
  { id: 'sequel_3', name: 'Franchise', description: 'Release 3 sequels', icon: '🔄',
    check: (s) => s.products.filter((p) => p.sequelNumber > 0).length >= 3 },
  { id: 'multi_project', name: 'Multitasker', description: 'Have 3 projects running simultaneously', icon: '⚡',
    check: (s) => s.projects.filter((p) => p.phase !== 'complete').length >= 3 },
  { id: 'all_equipment', name: 'Fully Equipped', description: 'Buy all equipment', icon: '🛠',
    check: (s) => (s.purchasedEquipment?.length ?? 0) >= 10 },
];

/** Get all achievement definitions (for the UI). */
export function getAllAchievements(): AchievementDef[] {
  return ACHIEVEMENTS;
}

export function achievementSystemUpdate(
  state: SimulationState,
  _themeData: ThemeData,
  _rng: SeededRandom,
): GameEvent[] {
  const events: GameEvent[] = [];
  if (!state.achievements) state.achievements = [];

  for (const ach of ACHIEVEMENTS) {
    if (state.achievements.includes(ach.id)) continue;
    if (ach.check(state)) {
      state.achievements.push(ach.id);
      events.push({
        type: 'ACHIEVEMENT_UNLOCKED',
        id: ach.id,
        name: ach.name,
        description: ach.description,
        icon: ach.icon,
      });
    }
  }

  return events;
}
